import {
    useContext,
    useEffect,
    useState
} from "react";
import {
    useWindowDimensions,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    PageContainer,
    Avatar,
    Text
} from "ncore-ui-kit";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
    AuthContext
} from "../../contexts/AuthContext";
import RootOrganizationSelector from "../../components/RootOrganizationSelector";
import OrganizationSwitcher from "../../components/OrganizationSwitcher";
import InstallPwa from "../../components/installPwa";

const Dashboard = () => {
    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const {
        activeOrganizationId,
        user
    } = useContext(AuthContext);

    const [
        activeOrgName,
        setActiveOrgName
    ] = useState("");

    const [
        activeOrgLogo,
        setActiveOrgLogo
    ] = useState("");

    useEffect(() => {
        if(!activeOrganizationId) {
            setActiveOrgName("");
            setActiveOrgLogo("");
            return;
        }

        if(user?.role !== "root") {
            const org = user?.organizations?.find(o => o.id === activeOrganizationId);
            setActiveOrgName(org?.name || "");

            const fetchOrg = async () => {
                try {
                    const token = await AsyncStorage.getItem("authToken");
                    const response = await fetch(`${process.env.API_URL}/organizations/${activeOrganizationId}`, {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    });
                    const result = await response.json();
                    if (result.status === "success") {
                        setActiveOrgLogo(result.data.profilePicturePath || result.data.logo || "");
                    }
                } catch (e) {
                    console.error(e);
                }
            };
            fetchOrg();
            return;
        }

        const fetchOrg = async () => {
            try {
                const token = await AsyncStorage.getItem("authToken");
                const response = await fetch(`${process.env.API_URL}/organizations?limit=100`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                const result = await response.json();
                if (result.status === "success") {
                    const org = result.data.find((o: { id: string; name: string; profilePicturePath?: string; logo?: string }) => o.id === activeOrganizationId);
                    setActiveOrgName(org?.name || "");
                    setActiveOrgLogo(org?.profilePicturePath || org?.logo || "");
                }
            } catch (e) {
                console.error(e);
            }
        };
        fetchOrg();
    }, [
        activeOrganizationId,
        user
    ]);

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const getRoleText = (role?: string) => {
        switch (role) {
            case "root":
                return localize("role-root");
            case "superAdmin":
                return localize("role-superadmin");
            case "admin":
                return localize("role-admin");
            case "user":
                return localize("role-user");
            case "guest":
                return localize("role-guest");
            default:
                return role || "";
        }
    };

    const renderContent = () => {
        if (user?.role === "root") {
            return <View
                style={[
                    stylesheet.card,
                    {
                        backgroundColor: colors.content.container.mid,
                        borderRadius: radiuses.md,
                        padding: spaces.spacingMd
                    }
                ]}
            >
                <Text
                    style={stylesheet.unselectableText}
                    variant="titleMediumSize"
                >
                    {localize("dashboard-role-root-desc")}
                </Text>
            </View>;
        }
        if (user?.role === "superAdmin") {
            return <View
                style={[
                    stylesheet.card,
                    {
                        backgroundColor: colors.content.container.subtle,
                        borderRadius: radiuses.md,
                        padding: spaces.spacingXl
                    }
                ]}
            >
                <Text
                    style={stylesheet.unselectableText}
                    variant="titleMediumSize"
                >
                    {localize("dashboard-role-superadmin-desc")}
                </Text>
            </View>;
        }
        if (user?.role === "admin") {
            return <View
                style={[
                    stylesheet.card,
                    {
                        backgroundColor: colors.content.container.subtle,
                        borderRadius: radiuses.md,
                        padding: spaces.spacingXl
                    }
                ]}
            >
                <Text
                    style={stylesheet.unselectableText}
                    variant="titleMediumSize"
                >
                    {localize("dashboard-role-admin-desc")}
                </Text>
            </View>;
        }

        return <View
            style={[
                stylesheet.card,
                {
                    backgroundColor: colors.content.container.subtle,
                    borderRadius: radiuses.md,
                    padding: spaces.spacingXl
                }
            ]}
        >
            <Text
                style={stylesheet.unselectableText}
                variant="titleMediumSize"
            >
                {localize("dashboard-role-guest-desc")}
            </Text>
        </View>;
    };

    return <PageContainer
        isScrollable={true}
    >
        <View
            style={[
                stylesheet.container,
                {
                    padding: spaces.spacingXl
                }
            ]}
        >
            {
                activeOrganizationId && activeOrgName ?
                    <Avatar
                        style={[
                            stylesheet.avatar,
                            {
                                borderRadius: isMobile ? 125 : 175,
                                marginBottom: spaces.spacingLg,
                                height: isMobile ? 250 : 350,
                                width: isMobile ? 250 : 350
                            }
                        ]}
                        imageUrl={activeOrgLogo}
                        title={activeOrgName}
                        size="xxxLarge"
                    />
                :
                    null
            }
            <Text
                style={stylesheet.unselectableText}
                variant="titleLargeSize"
            >
                {localize("dashboard-welcome", [
                    user?.role === "guest" ? localize("guest-user") : (user?.username || "")
                ])}
            </Text>
            <Text
                style={[
                    stylesheet.roleText,
                    {
                        marginTop: spaces.spacingMd
                    }
                ]}
                variant="bodyLargeSize"
            >
                {localize("dashboard-role-info", [
                    getRoleText(user?.role)
                ])}
            </Text>
            <View
                style={[
                    stylesheet.installPwaContainer,
                    {
                        marginTop: spaces.spacingXl
                    }
                ]}
            >
                <InstallPwa
                    spreadBehaviour="stretch"
                />
            </View>
            <View
                style={[
                    stylesheet.contentContainer,
                    {
                        marginTop: spaces.spacingXl
                    }
                ]}
            >
                {renderContent()}
            </View>
            <View
                style={[
                    stylesheet.buttonContainer,
                    {
                        width: isMobile ? "100%" : 350,
                        marginTop: spaces.spacingXl,
                        gap: spaces.spacingMd
                    }
                ]}
            >
                {
                    user?.role !== "guest" ?
                        (user?.role === "root" ?
                            <RootOrganizationSelector
                                spreadBehaviour="stretch"
                            />
                        :
                            <OrganizationSwitcher
                                spreadBehaviour="stretch"
                            />)
                    :
                        null
                }
            </View>
        </View>
    </PageContainer>;
};

export default Dashboard;
